import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * A horizontally scrolling row of product cards in the cart drawer, with previous / next
 * buttons that scroll by one visible width.
 *
 * The buttons use aria-disabled at either end rather than disabled, so a keyboard user's
 * focus stays on the button. The list resets to the start when the carousel's content
 * changes (data-carousel-key), for example when the cart crosses the free shipping
 * threshold and the products switch.
 *
 * Section re-renders morph this element in place without calling updatedCallback, so a
 * MutationObserver re-syncs the buttons and scroll position after each one.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} list - The scrolling list of slides.
 * @property {HTMLButtonElement} previous - The previous button.
 * @property {HTMLButtonElement} next - The next button.
 *
 * @extends {Component<Refs>}
 */
class CartDrawerCarousel extends Component {
  requiredRefs = ['list', 'previous', 'next'];

  /** @type {HTMLElement | null} */
  #observedList = null;
  #resizeObserver = new ResizeObserver(() => this.#updateButtons());
  #mutationObserver = new MutationObserver(() => this.#scheduleSync());
  #syncScheduled = false;
  /** @type {string | undefined} */
  #key;

  connectedCallback() {
    super.connectedCallback();
    this.#key = this.dataset.carouselKey;
    this.#observeList();
    this.#updateButtons();

    this.#mutationObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-carousel-key', 'aria-disabled'],
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#mutationObserver.disconnect();
    this.#resizeObserver.disconnect();
    this.#observedList?.removeEventListener('scroll', this.#handleScroll);
    this.#observedList = null;
  }

  /** @param {Event} event */
  previous(event) {
    event.preventDefault();
    this.#scrollByPage(-1);
  }

  /** @param {Event} event */
  next(event) {
    event.preventDefault();
    this.#scrollByPage(1);
  }

  #scheduleSync() {
    if (this.#syncScheduled) return;
    this.#syncScheduled = true;

    requestAnimationFrame(() => {
      this.#syncScheduled = false;

      if (this.dataset.carouselKey !== this.#key) {
        this.#key = this.dataset.carouselKey;
        this.refs.list?.scrollTo({ left: 0, behavior: 'instant' });
      }

      this.#observeList();
      this.#updateButtons();
    });
  }

  /** @param {1 | -1} direction */
  #scrollByPage(direction) {
    const { list } = this.refs;
    if (this.#atEnd(direction)) return;

    list.scrollBy({
      left: direction * list.clientWidth,
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
    });
  }

  /**
   * @param {1 | -1} direction
   * @returns {boolean}
   */
  #atEnd(direction) {
    const { list } = this.refs;
    if (!list) return true;

    const tolerance = 2;
    if (direction < 0) return list.scrollLeft <= tolerance;
    return list.scrollLeft + list.clientWidth >= list.scrollWidth - tolerance;
  }

  #observeList() {
    const { list } = this.refs;
    if (!list || list === this.#observedList) return;

    this.#observedList?.removeEventListener('scroll', this.#handleScroll);
    this.#resizeObserver.disconnect();

    list.addEventListener('scroll', this.#handleScroll, { passive: true });
    this.#resizeObserver.observe(list);
    this.#observedList = list;
  }

  #handleScroll = () => {
    this.#updateButtons();
  };

  #updateButtons() {
    const { previous, next } = this.refs;
    if (!previous || !next) return;

    // Only write changes: writing an unchanged value would still trigger the observer.
    const previousDisabled = String(this.#atEnd(-1));
    const nextDisabled = String(this.#atEnd(1));
    if (previous.getAttribute('aria-disabled') !== previousDisabled) previous.setAttribute('aria-disabled', previousDisabled);
    if (next.getAttribute('aria-disabled') !== nextDisabled) next.setAttribute('aria-disabled', nextDisabled);
  }
}

if (!customElements.get('cart-drawer-carousel')) {
  customElements.define('cart-drawer-carousel', CartDrawerCarousel);
}
